import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { API, useBranding } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Loader2, Mail, CheckCircle } from 'lucide-react';
import { useTheme } from '@/components/ThemeProvider';

const ForgotPasswordPage = () => {
  const { brand_name, logo_data, logo_dark_data, logo_light_data } = useBranding();
  const { theme } = useTheme();
  const activeLogo = (theme === 'dark' ? logo_dark_data : logo_light_data) || logo_data;

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error('Please enter your email address'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setSent(true);
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="forgot-password-page">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent" />

      <div className="w-full max-w-md relative z-10">
        <Link to="/login" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4" />
          Back to Sign In
        </Link>

        <div className="flex items-center gap-2 mb-8">
          {activeLogo ? (
            <img src={activeLogo} alt={brand_name} className="h-9 max-w-[140px] object-contain" />
          ) : (
            <>
              <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                <span className="text-white font-bold text-xl">C</span>
              </div>
              <span className="font-bold text-2xl">{brand_name || 'CoinPayUp'}</span>
            </>
          )}
        </div>

        <div className="bg-card border border-border rounded-xl p-8">
          {sent ? (
            /* Success state */
            <div className="text-center space-y-4" data-testid="forgot-password-success">
              <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              </div>
              <h2 className="text-xl font-bold text-foreground">Check your inbox</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                If <span className="text-foreground font-medium">{email}</span> is registered, we've sent a password reset link. It expires in <strong>1 hour</strong>.
              </p>
              <p className="text-xs text-muted-foreground">
                Didn't receive it? Check your spam folder or{' '}
                <button
                  onClick={() => setSent(false)}
                  className="text-primary underline hover:text-primary/80">
                  try again
                </button>.
              </p>
              <Link to="/login">
                <Button variant="outline" className="w-full mt-2" data-testid="back-to-login-btn">
                  Back to Sign In
                </Button>
              </Link>
            </div>
          ) : (
            /* Form state */
            <>
              <div className="mb-6">
                <div className="w-12 h-12 rounded-xl bg-primary/15 border border-primary/30 flex items-center justify-center mb-4">
                  <Mail className="w-5 h-5 text-primary" />
                </div>
                <h1 className="text-xl font-bold text-foreground">Forgot your password?</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Enter your email and we'll send you a reset link.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email">Email address</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="you@business.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    data-testid="forgot-email-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-primary text-white"
                  disabled={loading}
                  data-testid="forgot-password-submit-btn"
                >
                  {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Sending...</> : 'Send Reset Link'}
                </Button>
              </form>

              <p className="text-center text-sm text-muted-foreground mt-4">
                Remember your password?{' '}
                <Link to="/login" className="text-primary hover:underline">Sign in</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
